import { TranscriptWord } from "./TranscriptWord";
import { themeColors } from "@/utils/themeColors";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { Transcript, Utterance } from "@knowt/syncing/types/common";
import { areUtterancesEqual, formatTimestamp } from "../utils";
import { TranscriptEnum } from "@knowt/syncing/graphql/schema";

export type ChapterWithUtterance = Transcript[number] & { utterances: Utterance };

type Props = {
    chapter: ChapterWithUtterance;
    onWordClick: (utterance: Utterance[number]) => void;
    onTimestampClick: (timestamp: number) => void;
    activeSegment?: Utterance[number];
    transcriptType: TranscriptEnum;
};

export const TranscriptChapter = ({ chapter, activeSegment, onWordClick, onTimestampClick, transcriptType }: Props) => {
    const paragraphRef = useRef<HTMLParagraphElement>(null);

    const shouldHighlightByParagraph = useMemo(() => {
        return chapter.content.split(/\s+/).filter(word => word.trim().length > 0).length > chapter.utterances.length;
    }, [chapter.content, chapter.utterances.length]);

    const isParagraphActive = useMemo(() => {
        if (!activeSegment || !shouldHighlightByParagraph) return false;

        return activeSegment.start >= chapter.start && activeSegment.end <= chapter.end;
    }, [activeSegment, chapter.end, chapter.start, shouldHighlightByParagraph]);

    const wordUtterancePairs = useMemo(
        () => chapter.content.split(/\s+/).map((word, index) => ({ word, utterance: chapter.utterances[index] })),
        [chapter.content, chapter.utterances]
    );

    const chapterTimestamp = useMemo(() => formatTimestamp(chapter.start), [chapter.start]);

    useEffect(() => {
        if (shouldHighlightByParagraph && isParagraphActive) {
            paragraphRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        }
    }, [isParagraphActive, shouldHighlightByParagraph]);

    return (
        <div
            style={{
                display: "flex",
                paddingBlock: "1.5rem",
                paddingInline: "2.5rem",
                alignItems: "flex-start",
                gap: "15px",
                alignSelf: "stretch",
            }}
            data-testid="auto-id-0b3f494f-be76-47ca-b11b-2f11cff77114">
            {transcriptType === TranscriptEnum.FULL && (
                <span
                    onClick={() => onTimestampClick(chapter.start)}
                    style={{
                        display: "flex",
                        paddingBlock: "0.2rem",
                        paddingInline: "0.6rem",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "1rem",
                        borderRadius: "0.5rem",
                        backgroundColor: themeColors.primaryLight,
                        fontFeatureSettings: `"tnum"`,
                        fontSize: "1.4rem",
                        fontWeight: 700,
                        lineHeight: "2rem",
                        cursor: "pointer",
                        color: themeColors.pureBlack,
                    }}
                    data-testid="auto-id-712b0131-18ba-45e2-bf9d-101bf3667f5c">
                    {chapterTimestamp}
                </span>
            )}
            <p
                ref={paragraphRef}
                className="body1"
                style={{
                    whiteSpace: "break-spaces",
                    color: themeColors.neutralBlack,
                    scrollMarginBlock: "2.5rem",
                    borderRadius: "0.4rem",
                    ...(isParagraphActive
                        ? {
                              cursor: "pointer",
                              backgroundColor: themeColors.primaryLight,
                              outlineColor: themeColors.primaryLight,
                              outlineStyle: "solid",
                              outlineWidth: "4px",
                          }
                        : {}),
                }}
                data-testid="auto-id-1b813bf1-9127-4d51-b074-f27562e76f5f">
                {wordUtterancePairs.map(({ word, utterance }, i) => {
                    return (
                        <Fragment key={i}>
                            <TranscriptWord
                                word={word}
                                onClick={() => onWordClick(utterance)}
                                highlighted={
                                    !shouldHighlightByParagraph && activeSegment
                                        ? areUtterancesEqual(activeSegment, utterance)
                                        : false
                                }
                            />
                            <span data-testid="auto-id-01857538-86e7-4d12-9931-9bd088ebca5e"> </span>
                        </Fragment>
                    );
                })}
            </p>
        </div>
    );
};
